import {
  type MessageInitShape,
  type MessageShape,
  create,
} from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import {
  createConnectQueryKey,
  useMutation,
  useTransport,
} from '@connectrpc/connect-query'
import {
  listWorkflowTemplates,
  updateWorkflowTemplateDisplayOrder,
} from '@ebalance/proto/bffuser/v1/workflow_template_service-WorkflowTemplateService_connectquery'
import {
  ListWorkflowTemplatesRequestSchema,
  type ListWorkflowTemplatesResponse,
  ListWorkflowTemplatesResponseSchema,
  UpdateWorkflowTemplateDisplayOrderRequestSchema,
  UpdateWorkflowTemplateDisplayOrderResponseSchema,
} from '@ebalance/proto/bffuser/v1/workflow_template_service_pb'
import { useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult } from '@tanstack/react-query'

type MutationContext = { previous: ListWorkflowTemplatesResponse | undefined }

/**
 * ワークフロー雛形一覧の表示順を更新する mutation hook。
 *
 * - 楽観更新: mutate 直後に query cache 上のテンプレート一覧を並び替えて即座に反映。
 * - 失敗時は snapshot へ rollback。
 * - 成功後は list query を await invalidate して server state と再同期。invalidate
 *   が完了するまでは isPending=true を維持し、呼び出し側で編集・削除を無効化できる。
 */
export function useUpdateWorkflowTemplateDisplayOrder(): UseMutationResult<
  MessageShape<typeof UpdateWorkflowTemplateDisplayOrderResponseSchema>,
  ConnectError,
  MessageInitShape<typeof UpdateWorkflowTemplateDisplayOrderRequestSchema>
> {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const queryKey = createConnectQueryKey({
    schema: listWorkflowTemplates,
    cardinality: 'finite',
    input: create(ListWorkflowTemplatesRequestSchema, {}),
    transport,
  })

  return useMutation(updateWorkflowTemplateDisplayOrder, {
    onMutate: async (
      req: MessageInitShape<
        typeof UpdateWorkflowTemplateDisplayOrderRequestSchema
      >,
    ): Promise<MutationContext> => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<
        ListWorkflowTemplatesResponse | undefined
      >(queryKey)

      if (previous !== undefined && req.orderedTemplateIds !== undefined) {
        const byId = new Map(previous.templates.map((t) => [t.id, t]))
        const ordered = req.orderedTemplateIds
          .map((id) => byId.get(id))
          .filter(
            (t): t is ListWorkflowTemplatesResponse['templates'][number] =>
              t !== undefined,
          )

        queryClient.setQueryData<ListWorkflowTemplatesResponse | undefined>(
          queryKey,
          (old) => {
            if (old === undefined) return old
            return create(ListWorkflowTemplatesResponseSchema, {
              templates: ordered,
            })
          },
        )
      }

      return { previous }
    },
    onError: (_err, _req, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSuccess: async () => {
      // 並べ替え成功後は list query を再取得し、各テンプレートの updatedAt を
      // 最新化してから編集・削除操作を有効にする。それまでは isPending=true のため
      // 呼び出し側は action を disabled にできる。
      await queryClient.invalidateQueries({ queryKey })
    },
  })
}
